import type { MergedAudienceMember } from '@/types/audience-list-members.types';
import { WorkingPasscodeList } from '../components/admin/create/registration/passcode-list';
import { PasscodeList, PasscodeListMemberType, PASSCODE_LIST_TYPE } from '../types/working-model';
import { Put, Get, Post, Delete, FetchError, DeleteJson, getToken, PostJson } from './helpers';

export const GetAssociatedEvents = async (token: string, passcode_list: number, channel: number): Promise<string[]> => {
	return Post(`/v3/admin/content/associated-events`, token, { passcode_list, channel }, false, {}, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}
			return [];
		});
};

export const GetAssociatedEventsDetails = async (token: string, event_list: string[], channel: number): Promise<({ name: string, uuid: string }[])> => {
	if (event_list.length === 0) return [];
	return Post(`/v3/admin/content/associated-events-details`, token, { event_list, channel }, false, {}, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}
			return [];
		});
};

export const GetLinkedEmailToEvents = async (token: string, email: string, channel: number): Promise<string[]> => {
	if (!email || !channel) return [];
	return Post(`/v3/admin/content/linked-emails-to-events`, token, { email, channel }, false, {}, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}
			return [];
		});
};


export async function GetMagicLinksListTemplate(token: string, event_uuid: string): Promise<({ csv: string })> {
	return Post(`/v3/admin/events/${event_uuid}/magic-links`, token, {});
}

export const GetPasscodeLists = async (channel: number, token: string): Promise<any> => {
	return Get(`/v3/admin/content/passcode-lists/${channel}`, token, {}, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}

			throw new FetchError({ error: res.statusText, status: res.status });
		});
};

export const GetPasscodeList = async (token: string, channel: number, passcode_list: number): Promise<any> => {
	return Get(`/v3/admin/content/passcode-lists/${channel}/${passcode_list}`, token, {}, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}

			throw new FetchError({ error: res.statusText, status: res.status });
		});
};


export const CreateNewPasscodeList = async (token: string, channel: number, passcode_list: WorkingPasscodeList, uuid?: string, list_id?: number, dma_id?: string | null): Promise<any> => {
	if (passcode_list.type === PASSCODE_LIST_TYPE.MAGIC_LINKS || passcode_list.type === PASSCODE_LIST_TYPE.MAGIC_LINKS_REGISTRATION) {
		return PostJson({
			path: `/v3/admin/events/${uuid}/magic-links/upload`,
			token,
			data: { ...passcode_list, channel: channel, list_id: list_id, dma_id },
		});
	} else if (passcode_list.url === '') {
		return PostJson({
			path: `/v3/admin/content/passcode-lists/no-members`,
			token,
			data: { ...passcode_list, channel: channel, dma_id },
		});
	}
	else {
		return PostJson({
			path: `/v3/admin/content/passcode-lists`,
			token,
			data: { ...passcode_list, channel: channel, dma_id },
		});
	}
};

export function UpdateMaxUses(
	token: string,
	uses: number,
	list: number | undefined
): Promise<any> {
	if (list) {
		return Put(
			`/v3/admin/content/passcode-lists/update-list/max-uses`,
			token,
			{
				uses,
				list,
			}
		);
	}
	else { return Promise.resolve(); }
}

export function UpdateMasterPasscode(
	token: string,
	passcode: string,
	list: number | undefined
): Promise<any> {
	if (list) {
		return Put(
			`/v3/admin/content/passcode-lists/update-list/master-passcode`,
			token,
			{ passcode, list }
		);
	}
	else { return Promise.resolve(); }
}

export const UploadMembers = async (token: string, channel: number, filename: string, passcode_list: number, type: number, url: string): Promise<any> => {
	return Post('/v3/admin/content/passcode-lists/members', token, { filename, passcode_list, type, url, channel }, false, {}, true)
		.then(async res => {
			if (res.ok) {
				return await res.json();
			}

			throw new FetchError({ error: res.statusText, status: res.status });
		});
};

export const DeleteMembers = async (members: MergedAudienceMember[], token: string): Promise<{ deleted: number }> => {
	const isV2 = members[0]?.internal_source === 'audience_list_member';
	if (isV2) {
		return DeleteJson<{ passcode_list: number, bl_profiles: number[] }, { deleted: number }>({
			path: '/v3/admin/content/audience-lists/removemember',
			data: {
				passcode_list: members[0].passcode_list,
				bl_profiles: members.map(member => member.member_id)
			}
		});
	}

	const membersToDelete: number[] = members.map((member) => member.member_id);
	await Delete('/v3/admin/content/passcode-lists/removemember', token, { passcodeListMembers: membersToDelete }, false, true);
	return { deleted: membersToDelete.length };
};

export async function GetPasscodeListEncrypt(token: string, channel: number, passcodelist: number): Promise<any> {
	return Get(`/v3/admin/content/passcode-lists/encrypt/${channel}/${passcodelist}`, token);
}

export async function DeletePasscodeList(token: string, channel: number, list: PasscodeList): Promise<any> {
	return Put(`/v3/admin/content/passcode-lists/delete`, token, { channel, list }, true, false);
}

export const DeleteDuplicateMembers = async (channel: number, list: number): Promise<any> => {
	return DeleteJson({
		path: '/v3/admin/content/passcode-lists/remove-duplicate-members',
		token: getToken(),
		data: { list, channel }
	});
};

export const SendUnsentMagicLinkRegistrationEmails = async (token: string, eventUuid: string, passcode_list: number): Promise<any> => {
	return PostJson(
		{ path: `/v3/admin/send-now-emails/send-magic-link/${eventUuid}`, token: token, data: { passcodeListId: passcode_list } }
	);
};

export const ResetMagicLinkEmailStatus = async (token: string, eventUuid: string, passcode_list: number): Promise<any> => {
	return PostJson(
		{ path: `/v3/admin/send-now-emails/reset-magic-link-send-status/${eventUuid}`, token: token, data: { passcodeListId: passcode_list } }
	);
};
